import { ActionButton, merge, Stack, StackItem, Text } from '@fluentui/react';
import React, { useCallback, useMemo } from 'react';
import replyIcon from '../../../assets/icons/svg/reply.svg';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';
import { useTchatManager } from '../../../contexts/tchatContext';
import Attachment from './Attachment';
import Avatar from './Avatar';

const Message = (props) => {

    const {
        dateEnvoi = undefined,
        idMessage = undefined,
        idMessageParent = undefined,
        loginCompte = undefined,
        piecesJointes = [],
        searchedValue = undefined,
        texte = undefined,
        texteMessageParent = undefined,
    } = props;

    const { responsiveSize } = useResponsiveSize();
    const { updateMessage } = useTchatManager();

    const estMessageEiffage = useMemo(() => !!loginCompte, [loginCompte]);

    const styles = useMemo(
        () => {

            const messageStyle = {
                root: {
                    color: "#777777",
                    fontSize: 13,
                    lineHeight: "16px",
                    textAlign: "justify"
                }
            };

            const quoteStyle = {
                root: {
                    borderBottom: "1px solid #D9D9D9",
                    borderTop: "1px solid #D9D9D9",
                    color: "#777777",
                    fontSize: 13,
                    fontStyle: "italic",
                    lineHeight: "16px"
                }
            };

            const highlightStyle = {
                root: {
                    backgroundColor: "yellow",
                    fontWeight: 700
                }
            };

            let styles = {
                attachments: {
                    root: {
                        borderBottom: estMessageEiffage ? "1px solid #D9D9D9" : "1px solid white",
                        borderTop: estMessageEiffage ? "1px solid #D9D9D9" : "1px solid white",
                        padding: "12px 0"
                    }
                },
                date: {
                    root: {
                        color: "#777777",
                        fontSize: 10,
                        lineHeight: "12px",
                    }
                },
                highlightedMessage: { root: { ...messageStyle.root, ...highlightStyle.root } },
                highlightedQuote: { root: { ...quoteStyle.root, ...highlightStyle.root } },
                message: messageStyle,
                outer: {
                    root: {
                        alignSelf: estMessageEiffage ? "end" : "start",
                        backgroundColor: estMessageEiffage ? "white" : "#DADADA",
                        border: "1px solid #EEEEEE",
                        borderRadius: 13,
                        gap: 13,
                        padding: 13,
                        width: "80%"
                    }
                },
                quote: quoteStyle,
                replyButton: {
                    root: {
                        color: "#777777",
                        fontSize: 11,
                        height: "unset",
                        lineHeight: "13px",
                        position: "absolute",
                        right: 0,
                        top: 0,
                    },
                    label: {
                        marginBottom: 2,
                        marginLeft: 0,
                    }
                },
                sender: {
                    root: {
                        color: estMessageEiffage ? "black" : "#777777",
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: "14px",
                    }
                },
            };

            if (responsiveSize >= ResponsiveSize.medium)
                styles = merge(
                    styles,
                    {
                        outer: {
                            root: {
                                backgroundColor: estMessageEiffage ? "white" : "#EEEEEE",
                                marginLeft: 27,
                                marginRight: 27,
                            }
                        },
                    }
                );

            return styles;
        },
        [estMessageEiffage, responsiveSize]
    );

    const textWithLineBreakAndHighlight = useMemo(() => {
        if (!texte) return texte;
        else {
            const regex = new RegExp(`(${searchedValue})`, "gi");
            // Split sur les retours chariots et retourner une ligne (span) pour chacun des éléments du tableau résultant
            // Si recherche effectuée, split sur la ligne et application d'un style particulier sur le texte recherché
            return texte.split("\n").filter(row => !!row).map((row, rowIndex) => {
                if (!!searchedValue && searchedValue.length > 2) {
                    const parts = row.split(regex).filter(part => !!part);
                    return <StackItem key={rowIndex}>
                        {parts.map((part, partIndex) => {
                            const dataHighlighted = part.toLowerCase() === searchedValue.toLowerCase();
                            const style = dataHighlighted ? styles.highlightedMessage : styles.message;
                            return <Text key={partIndex} styles={style} data-highlighted={dataHighlighted}>{part}</Text>
                        })}
                    </StackItem>;
                }
                else return <Text styles={styles.message}>{row}</Text>;
            })
        }
    }, [searchedValue, styles, texte]);

    const texteMessageParentWithHighlight = useMemo(() => {
        if (!texteMessageParent)
            return <StackItem styles={styles.quote}>En réponse au message : &lt; Pièce jointe uniquement &gt;</StackItem>

        else if (!searchedValue || searchedValue.length <= 2)
            return <StackItem styles={styles.quote}>En réponse au message : {texteMessageParent}</StackItem>

        else {
            const regex = new RegExp(`(${searchedValue})`, "gi");
            return <StackItem>
                {texteMessageParent.split(regex).filter(part => !!part).map((part, index) => {
                    const dataHighlighted = part.toLowerCase() === searchedValue.toLowerCase();
                    const style = dataHighlighted ? styles.highlightedQuote : styles.quote;
                    return <Text key={index} styles={style} data-highlighted={dataHighlighted}>{part}</Text>
                })}
            </StackItem>;
        }
    }, [searchedValue, texteMessageParent, styles]);

    const onReply = useCallback(() => {
        updateMessage({
            idMessageParent: idMessage,
            texteMessageParent: texte
        });
    }, [idMessage, texte, updateMessage]);

    return <Stack styles={styles.outer}>

        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 15 }} styles={{ root: { position: "relative" } }}>
            <Avatar estEiffage={estMessageEiffage} />
            <Stack>
                {estMessageEiffage && <Text styles={styles.sender}>Votre conseiller Eiffage Immobilier</Text>}
                {!!dateEnvoi && <Text styles={styles.date}>{new Date(dateEnvoi).toLocaleString("fr-FR", { year: "numeric", month: "long", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</Text>}
            </Stack>
            {estMessageEiffage &&
                <ActionButton
                    iconProps={{ imageProps: { src: replyIcon } }}
                    onClick={onReply}
                    styles={styles.replyButton}
                >
                    {responsiveSize >= ResponsiveSize.medium && "Répondre"}
                </ActionButton>
            }
        </Stack>

        {Array.isArray(piecesJointes) && piecesJointes.length > 0 &&
            <Stack styles={styles.attachments}>
                {piecesJointes.map((pieceJointe, index) => {
                    const { nom, rowGUID } = pieceJointe;
                    return <Attachment fileName={nom} rowGUID={rowGUID} searchedValue={searchedValue} />;
                })}
            </Stack>
        }

        {!!idMessageParent && texteMessageParentWithHighlight}

        {!!textWithLineBreakAndHighlight && <Stack>{textWithLineBreakAndHighlight}</Stack>}

    </Stack>;

};

export default Message;